/**
 * ViewToggle — header pill that switches between the standalone Vera
 * view (`#/`) and the Forma-embedded mock view (`#/forma`).
 *
 * Used in both views' headers so demo viewers can flip between them
 * without typing a URL. The hash router (useHashRoute) re-renders
 * the top-level shell when the hash changes — this component just
 * sets the hash and lets the router do the work.
 *
 * Visual style is theme-aware: dark accent in the standalone view,
 * Forma-blue accent in the Forma view. The component reads the
 * current route to highlight the active pill.
 */
import { type RouteName, setRoute, useHashRoute } from "../hooks/useHashRoute";

export function ViewToggle({
  variant = "dark",
}: {
  variant?: "dark" | "light";
}) {
  const current = useHashRoute();
  const isLight = variant === "light";

  return (
    <div
      role="tablist"
      aria-label="Switch demo surface"
      style={{
        display: "inline-flex",
        background: isLight ? "#f2f3f5" : "rgba(255,255,255,0.04)",
        border: `1px solid ${isLight ? "#d8dde3" : "var(--fc2-border)"}`,
        borderRadius: 4,
        overflow: "hidden",
        fontSize: 11,
      }}
    >
      <Pill
        active={current === "standalone"}
        onClick={() => setRoute("standalone")}
        label="Standalone"
        variant={variant}
      />
      <Pill
        active={current === "forma"}
        onClick={() => setRoute("forma")}
        label="In Forma"
        variant={variant}
      />
    </div>
  );
}

function Pill({
  active,
  onClick,
  label,
  variant,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  variant: "dark" | "light";
}) {
  const isLight = variant === "light";
  const activeBg = isLight ? "#0a72e8" : "rgba(127,179,213,0.18)";
  const activeFg = isLight ? "#ffffff" : "#7fb3d5";
  const idleFg = isLight ? "#374151" : "var(--fc2-text)";
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        background: active ? activeBg : "transparent",
        color: active ? activeFg : idleFg,
        border: "none",
        padding: "4px 12px",
        cursor: "pointer",
        font: "inherit",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

export type { RouteName };
